// Entry type metadata — single source of truth for UI labels, icons, and
// the shape of `detail` payloads sent to Supabase.

import {
  ForkKnife,
  Bed,
  Baby,
  Toilet,
  PaintBrush,
  NotePencil,
} from "@phosphor-icons/react";

export const ENTRY_TYPES = Object.freeze({
  MEAL: "meal",
  NAP: "nap",
  DIAPER: "diaper",
  POTTY: "potty",
  ACTIVITY: "activity",
  NOTE: "note",
});

export const ENTRY_TYPE_LIST = [
  ENTRY_TYPES.MEAL,
  ENTRY_TYPES.NAP,
  ENTRY_TYPES.DIAPER,
  ENTRY_TYPES.POTTY,
  ENTRY_TYPES.ACTIVITY,
  ENTRY_TYPES.NOTE,
];

export const ENTRY_META = {
  [ENTRY_TYPES.MEAL]: {
    label: "Meal",
    icon: ForkKnife,
    color: "text-[#C6851E]",
    bg: "bg-[#C6851E]/10",
  },
  [ENTRY_TYPES.NAP]: {
    label: "Nap",
    icon: Bed,
    color: "text-[#5A7E46]",
    bg: "bg-primary/10",
  },
  [ENTRY_TYPES.DIAPER]: {
    label: "Diaper",
    icon: Baby,
    color: "text-[#9AA0A8]",
    bg: "bg-neutral-100",
  },
  [ENTRY_TYPES.POTTY]: {
    label: "Potty",
    icon: Toilet,
    color: "text-[#4A4E56]",
    bg: "bg-[#EFBA57]/30",
  },
  [ENTRY_TYPES.ACTIVITY]: {
    label: "Activity",
    icon: PaintBrush,
    color: "text-[#5A7E46]",
    bg: "bg-primary/10",
  },
  [ENTRY_TYPES.NOTE]: {
    label: "Note",
    icon: NotePencil,
    color: "text-neutral-700",
    bg: "bg-neutral-100",
  },
};

export const MEAL_OPTIONS = ["breakfast", "lunch", "snack", "dinner"];
export const MEAL_AMOUNTS = ["all", "most", "some", "none"];
export const DIAPER_KINDS = ["wet", "bm", "dry"];
export const POTTY_RESULTS = ["success", "attempt", "accident"];
export const NAP_QUALITY = ["sound", "restless", "short"];
export const MOODS = ["happy", "content", "sleepy", "fussy", "sad"];

// Human-readable one-liner summarising `detail` for list/card display.
export function summariseEntry(entry) {
  const d = entry.detail || {};
  switch (entry.entry_type) {
    case ENTRY_TYPES.MEAL:
      if (d.food_program) {
        return `${cap(d.meal || "Meal")} · ${d.substitution_logged ? "substitution served" : "served"}${d.items ? ` — ${d.items}` : ""}`;
      }
      return `${cap(d.meal || "Meal")} · ate ${d.amount || "some"}${d.items ? ` — ${d.items}` : ""}`;
    case ENTRY_TYPES.NAP: {
      const parts = [];
      if (d.start_time && d.end_time) parts.push(`${d.start_time}–${d.end_time}`);
      if (d.quality) parts.push(d.quality);
      return parts.length ? parts.join(" · ") : "Nap logged";
    }
    case ENTRY_TYPES.DIAPER:
      return `${cap(d.kind || "diaper")}${d.notes ? ` — ${d.notes}` : ""}`;
    case ENTRY_TYPES.POTTY:
      return cap(d.result || "potty");
    case ENTRY_TYPES.ACTIVITY:
      return d.title || "Activity";
    case ENTRY_TYPES.NOTE:
      return d.body || "Note";
    default:
      return entry.entry_type;
  }
}

function cap(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatTime(iso) {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function nowTimeStr() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function prettyDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}
